import { FC, useEffect } from 'react';
import { Stack } from '@mui/material';
import {
  Button,
  DataLossPrompt,
  Footer,
  Loader,
  MultiLanguageInputContainer,
  NothingFoundAlert,
  Panel,
  SelectInput,
  TextField,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { ContentLanguage } from 'types/general';
import { useCompanyLanguages } from 'state/Administrators';
import { useIdParam } from 'utils/useIdParam';
import {
  NewsCategoryTranslations,
  useCreateNewsCategory,
  useNewsCategory,
  useUpdateNewsCategory,
} from 'store/news';
import { usePredefinedToasts } from 'utils/Toast';
import { useHistory } from 'react-router-dom';
import { formatLanguage } from 'utils/format';

interface Values {
  name: string;
  languages: Array<Uppercase<ContentLanguage>>;
  translations: NewsCategoryTranslations;
}

const INITIAL_VALUES: Values = {
  name: '',
  languages: [],
  translations: [],
};

export const NewsCategoryDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const categoryId = useIdParam();
  const predefinedToasts = usePredefinedToasts();
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();

  const { data: category, isLoading } = useNewsCategory(categoryId);

  const {
    control,
    reset,
    watch,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: {
      ...INITIAL_VALUES,
      languages: [defaultLanguage],
      translations: [{ language: defaultLanguage, name: '' }],
    },
  });
  const { fields, replace } = useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        languages: category.translations.map(
          (translation) => translation.language,
        ),
        translations: category.translations,
      });
    }
  }, [category, reset]);

  const { mutate: createCategory, isLoading: isCreating } =
    useCreateNewsCategory();
  const { mutate: updateCategory, isLoading: isUpdating } =
    useUpdateNewsCategory();

  const onSubmit: SubmitHandler<Values> = ({ name, translations }) => {
    if (category) {
      updateCategory(
        { categoryId: category.id, name, translations },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
          },
        },
      );
    } else {
      createCategory(
        { name, translations },
        {
          onSuccess: () => {
            predefinedToasts.success.added();
            history.push(`${ROUTES.news.list}?tab=categories`);
          },
        },
      );
    }
  };

  const [translations] = watch(['translations']);

  if (categoryId && isLoading) return <Loader />;
  if (categoryId && !category) return <NothingFoundAlert />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('benefits.benefitGeneralSettingsSection')}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('benefitTemplates.templateContent')}>
            <Stack spacing={4}>
              <Controller
                name="languages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('companies.supported_languages')}
                      name={name}
                      value={value}
                      onChange={(value) => {
                        const selectedLanguages = value as Array<
                          Uppercase<ContentLanguage>
                        >;
                        replace(
                          selectedLanguages.map(
                            (language) =>
                              translations.find(
                                (t) => t.language === language,
                              ) ?? { language, name: '' },
                          ),
                        );
                        onChange(selectedLanguages);
                      }}
                      options={supportedLanguages.map((language) => ({
                        value: language,
                        label: formatLanguage(i18n.language, language),
                        disabled: language === defaultLanguage,
                      }))}
                      multiple
                      required
                      ref={ref}
                    />
                  );
                }}
              />

              <MultiLanguageInputContainer
                label={t('common.name')}
                fields={fields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <Controller
                      name={`translations.${i}.name`}
                      control={control}
                      render={({ field: { name, value, onChange, ref } }) => {
                        return (
                          <TextField
                            name={name}
                            value={value}
                            onChange={onChange}
                            required
                            ref={ref}
                          />
                        );
                      }}
                    />
                  ),
                }))}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isCreating || isUpdating}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty && !isCreating} />
    </>
  );
};
